import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Save, X, Crown } from 'lucide-react';

const ALL_PERMISSIONS = [
  { key: 'framework', label: 'Framework', description: 'View and manage strategic framework' },
  { key: 'codex', label: 'Codex', description: 'View and manage values, principles, red lines' },
  { key: 'org_chart', label: 'Org Chart', description: 'View organizational structure' },
  { key: 'org_chart_edit', label: 'Org Chart (Edit)', description: 'Edit engines, officers, assignments' },
  { key: 'user_access', label: 'User Access', description: 'Invite and manage users' },
  { key: 'rbac_management', label: 'RBAC Management', description: 'Manage role permissions' },
  { key: 'mission_control', label: 'Mission Control', description: 'View and manage missions' },
  { key: 'objectives', label: 'Objectives', description: 'View and manage objectives' },
  { key: 'activities', label: 'Activities', description: 'View and manage activities' },
  { key: 'daily_tasks', label: 'Daily Tasks', description: 'View and manage daily tasks' },
  { key: 'artifacts', label: 'Artifacts & Rituals', description: 'View and manage artifacts' },
  { key: 'issues', label: 'Issues & Policies', description: 'View and manage issues' },
  { key: 'documents', label: 'Document Library', description: 'View and manage documents' },
  { key: 'reports', label: 'Reports', description: 'View analytics and reports' },
  { key: 'task_execution', label: 'Task Execution', description: 'Execute assigned tasks' },
  { key: 'automation', label: 'Automation', description: 'Run automated processes' },
  { key: 'bot_skills_manage', label: 'Bot Skills (Manage)', description: '⚠️ Create, edit, and delete bot skills. Skills are injected into bot context and can alter bot behavior. Restricted by default.' },
];

const DEFAULT_ROLE_PERMISSIONS = {
  'Commander in Chief': ['framework', 'codex', 'org_chart', 'org_chart_edit', 'user_access', 'rbac_management', 'mission_control', 'objectives', 'activities', 'daily_tasks', 'artifacts', 'issues', 'documents', 'reports', 'task_execution', 'automation', 'bot_skills_manage'],
  'Commander': ['framework', 'codex', 'org_chart', 'org_chart_edit', 'user_access', 'mission_control', 'objectives', 'activities', 'daily_tasks', 'artifacts', 'issues', 'documents', 'reports', 'task_execution'],
  'Senior Officer': ['org_chart', 'mission_control', 'objectives', 'activities', 'daily_tasks', 'artifacts', 'issues', 'documents', 'reports', 'task_execution'],
  'Officer': ['mission_control', 'objectives', 'activities', 'daily_tasks', 'artifacts', 'issues', 'documents', 'task_execution'],
  'Analyst': ['framework', 'objectives', 'activities', 'daily_tasks', 'artifacts', 'issues', 'documents', 'reports'],
  'Operator': ['mission_control', 'activities', 'daily_tasks', 'artifacts', 'issues', 'documents', 'task_execution'],
  'AI Officer': ['task_execution', 'automation'],
};

const ROLE_STYLES = {
  'Commander in Chief': { bg: 'from-amber-900/20 to-amber-800/10', border: 'border-amber-500/30', badge: 'bg-amber-500/20 text-amber-400', text: 'text-amber-400' },
  'Commander': { bg: 'bg-purple-900/10', border: 'border-purple-500/30', badge: 'bg-purple-500/20 text-purple-400', text: 'text-purple-400' },
  'Senior Officer': { bg: 'bg-emerald-900/10', border: 'border-emerald-500/30', badge: 'bg-emerald-500/20 text-emerald-400', text: 'text-emerald-400' },
  'Officer': { bg: 'bg-blue-900/10', border: 'border-blue-500/30', badge: 'bg-blue-500/20 text-blue-400', text: 'text-blue-400' },
  'Analyst': { bg: 'bg-cyan-900/10', border: 'border-cyan-500/30', badge: 'bg-cyan-500/20 text-cyan-400', text: 'text-cyan-400' },
  'Operator': { bg: 'bg-slate-800/30', border: 'border-slate-600/30', badge: 'bg-slate-500/20 text-slate-400', text: 'text-slate-400' },
  'AI Officer': { bg: 'bg-indigo-900/10', border: 'border-indigo-500/30', badge: 'bg-indigo-500/20 text-indigo-400', text: 'text-indigo-400' },
};

export default function RolePermissionsEditor({ officers, currentUserEmail }) {
  const queryClient = useQueryClient();
  const [editingRole, setEditingRole] = useState(null);
  const [editedPermissions, setEditedPermissions] = useState([]);

  const { data: rbacConfigs, isLoading } = useQuery({
    queryKey: ['rbacConfigs'],
    queryFn: () => base44.entities.RBACConfig.list(),
    initialData: []
  });

  const createConfigMutation = useMutation({
    mutationFn: (data) => base44.entities.RBACConfig.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rbacConfigs'] })
  });

  const updateConfigMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.RBACConfig.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rbacConfigs'] })
  });

  const getPermissionsForRole = (roleName) => {
    const config = rbacConfigs.find(c => c.role_name === roleName);
    return config?.permissions || DEFAULT_ROLE_PERMISSIONS[roleName] || [];
  };

  const handleEditRole = (roleName) => {
    setEditingRole(roleName);
    setEditedPermissions(getPermissionsForRole(roleName));
  };

  const handleSavePermissions = async () => {
    const existingConfig = rbacConfigs.find(c => c.role_name === editingRole);
    
    if (existingConfig) {
      await updateConfigMutation.mutateAsync({
        id: existingConfig.id,
        data: {
          permissions: editedPermissions,
          updated_by: currentUserEmail
        }
      });
    } else {
      await createConfigMutation.mutateAsync({
        role_name: editingRole,
        permissions: editedPermissions,
        description: `Permissions for ${editingRole}`,
        updated_by: currentUserEmail
      });
    }
    setEditingRole(null);
  };

  const togglePermission = (permKey) => {
    setEditedPermissions(prev => 
      prev.includes(permKey) 
        ? prev.filter(p => p !== permKey)
        : [...prev, permKey]
    );
  };

  const roleOrder = ['Commander in Chief', 'Commander', 'Senior Officer', 'Officer', 'Analyst', 'Operator', 'AI Officer'];

  return (
    <div className="space-y-4">
      <div className="bg-slate-100 p-4 rounded-lg border border-slate-800">
        <h4 className="text-slate-600 mb-4 font-semibold flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-500" />
          Role Hierarchy & Permissions
        </h4>
        <div className="space-y-4">
          {roleOrder.map((roleName) => {
            const style = ROLE_STYLES[roleName];
            const permissions = getPermissionsForRole(roleName);
            const officerCount = roleName === 'Operator' 
              ? officers.filter(o => o.custom_role === 'Operator' || !o.custom_role).length
              : officers.filter(o => o.custom_role === roleName).length;
            const isBot = roleName === 'AI Officer';
            
            return (
              <div 
                key={roleName} 
                className={`p-4 ${roleName === 'Commander in Chief' ? 'bg-gradient-to-r ' + style.bg : style.bg} rounded-lg border ${style.border}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Badge className={`${style.badge} border-none`}>
                      {roleName === 'Commander in Chief' && '👑 '}
                      {roleName === 'AI Officer' && '🤖 '}
                      {roleName}
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {officerCount} {isBot ? 'bots' : 'officers'}
                    </span>
                  </div>
                  {roleName !== 'Commander in Chief' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditRole(roleName)}
                      className="h-7 px-2 text-slate-500 hover:text-slate-300"
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-400 mb-2">
                  {roleName === 'Commander in Chief' && 'Supreme access - All permissions (cannot be modified)'}
                  {roleName === 'Commander' && 'Full access to all features'}
                  {roleName === 'Senior Officer' && 'Officer access plus Org Chart visibility'}
                  {roleName === 'Officer' && 'Manage missions and objectives'}
                  {roleName === 'Analyst' && 'View and analyze data'}
                  {roleName === 'Operator' && 'Execute missions only'}
                  {roleName === 'AI Officer' && 'AI Co-workers (requires human owner)'}
                </p>
                <div className="flex flex-wrap gap-1">
                  {permissions.map(permKey => {
                    const perm = ALL_PERMISSIONS.find(p => p.key === permKey);
                    return perm ? (
                      <Badge 
                        key={permKey} 
                        variant="outline" 
                        className={`text-xs ${style.text} ${style.border}`}
                      >
                        {perm.label}
                      </Badge>
                    ) : null;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Permissions Dialog */}
      <Dialog open={!!editingRole} onOpenChange={(open) => !open && setEditingRole(null)}>
        <DialogContent data-testid="rbac-rolepermissionseditor-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Edit Permissions: {editingRole}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              Select the permissions granted to this role:
            </p>
            <div className="space-y-2">
              {ALL_PERMISSIONS.map((perm) => (
                <div 
                  key={perm.key}
                  className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-800/50 cursor-pointer"
                  onClick={() => togglePermission(perm.key)}
                >
                  <Checkbox
                    checked={editedPermissions.includes(perm.key)}
                    onCheckedChange={() => togglePermission(perm.key)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm text-slate-200">{perm.label}</div>
                    <div className="text-xs text-slate-500">{perm.description}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => setEditingRole(null)}
                variant="outline"
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSavePermissions}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                disabled={updateConfigMutation.isPending || createConfigMutation.isPending}
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}